import { rich, useT } from '@/i18n/runtime';
import type { TenantConfig } from '@/types';
import type { MediaPermissionsState } from '@/media/useMediaPermissions';
import { AppBar } from './AppBar';
import { Icon } from './Icon';
import { Button } from './Button';

/**
 * PERMISSION GATE — the camera/mic permission UX shown BEFORE the media join,
 * replacing the static "camera blocked" banner. Renders the machine in
 * useMediaPermissions:
 *
 *   • primer      → explain WHY, THEN trigger the native prompt from Allow.
 *   • requesting  → the browser prompt is up.
 *   • denied      → guided, browser-detected recovery + auto-resume (flip the
 *                   toggle and we rejoin without a reload). Honest: no site can
 *                   re-open the native prompt once denied.
 *   • os-denied   → the prompt never appeared → check system settings.
 *   • no-device   → no camera/mic present.
 *   • webview     → opened in an in-app browser (WhatsApp/Instagram/…): open in
 *                   Chrome/Safari + copy-link (the one that matters for droom).
 *   • unsupported → getUserMedia unavailable.
 *
 * Tokens/classes only (Dataroom mode) — no hardcoded colours.
 */
export function PermissionGate({
  tenant,
  perms,
}: { tenant: TenantConfig; perms: MediaPermissionsState }): JSX.Element {
  const { t } = useT();
  return (
    <div className="app-view">
      <AppBar tenant={tenant} subtitle={t('droom.live.subtitle')} />

      <div className="app-view center" style={{ paddingTop: 24 }}>
        <div className="center-col" style={{ maxWidth: '34ch' }}>
          <span className="perm-gate-badge">
            <Icon name={perms.phase === 'webview' ? 'monitor' : 'camera'} size={30} />
          </span>

          {perms.phase === 'checking' ? (
            <p className="sub" role="status">
              {t('droom.perm.checking')}
            </p>
          ) : null}

          {perms.phase === 'primer' ? (
            <>
              <div className="h-title sm">{t('droom.perm.primer.title')}</div>
              <p className="sub" style={{ marginTop: 8 }}>
                {rich(t('droom.perm.primer.body'))}
              </p>
              {/* Testmap #12 (owner mandate): a below-support-floor device is TOLD the minimum
                  here, before anything can fail — advisory, never a refusal. */}
              {perms.osNotice ? (
                <p className="sub-sm perm-os-notice" style={{ marginTop: 10 }} role="note">
                  {t(perms.osNotice.key, { min: perms.osNotice.min })}
                </p>
              ) : null}
              <Button style={{ marginTop: 18 }} onClick={perms.request}>
                {t('droom.action.continue')}
              </Button>
            </>
          ) : null}

          {perms.phase === 'requesting' ? (
            <>
              <div className="h-title sm">{t('droom.perm.requesting.title')}</div>
              <p className="sub" style={{ marginTop: 8 }} role="status">
                {rich(t('droom.perm.requesting.body'))}
              </p>
            </>
          ) : null}

          {perms.phase === 'denied' || perms.phase === 'os-denied' ? (
            <>
              <div className="h-title sm">
                {perms.phase === 'os-denied'
                  ? t('droom.perm.osdenied.title')
                  : t('droom.perm.denied.title')}
              </div>
              <p className="sub" style={{ marginTop: 8 }}>
                {perms.phase === 'os-denied'
                  ? t('droom.perm.osdenied.body')
                  : t('droom.perm.denied.body')}
              </p>
              <ol className="perm-steps" aria-label={t('droom.perm.steps.aria')}>
                {perms.recoverySteps.map((step) => (
                  <li key={step}>{t(step)}</li>
                ))}
              </ol>
              <p className="perm-autoresume mono" role="status">
                {t('droom.perm.autoresume')}
              </p>
              <Button style={{ marginTop: 12 }} onClick={perms.retry}>
                {t('droom.action.allowed')}
              </Button>
            </>
          ) : null}

          {perms.phase === 'no-device' ? (
            <>
              <div className="h-title sm">{t('droom.perm.nodevice.title')}</div>
              <p className="sub" style={{ marginTop: 8 }}>
                {perms.detail ? t(perms.detail) : t('droom.perm.nodevice.fallback')}{' '}
                {t('droom.perm.nodevice.advice')}
              </p>
              <Button style={{ marginTop: 18 }} onClick={perms.retry}>
                {t('droom.action.tryAgain')}
              </Button>
            </>
          ) : null}

          {perms.phase === 'webview' ? (
            <>
              <div className="h-title sm">{t('droom.perm.webview.title')}</div>
              <p className="sub" style={{ marginTop: 8 }}>
                {rich(t('droom.perm.webview.body'))}
              </p>
              <Button style={{ marginTop: 18 }} onClick={perms.copyLink}>
                {perms.linkCopied ? t('droom.action.linkCopied') : t('droom.action.copyLink')}
              </Button>
              <p className="perm-autoresume mono" style={{ marginTop: 10 }}>
                {t('droom.perm.webview.tip')}
              </p>
            </>
          ) : null}

          {perms.phase === 'unsupported' ? (
            <>
              <div className="h-title sm">{t('droom.perm.unsupported.title')}</div>
              <p className="sub" style={{ marginTop: 8 }}>
                {t('droom.perm.unsupported.body')}
              </p>
              <Button style={{ marginTop: 18 }} onClick={perms.copyLink}>
                {perms.linkCopied ? t('droom.action.linkCopied') : t('droom.action.copyLink')}
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
